import { GenderCode } from '@lib/types';
import { AssociateInfo, RoleType } from '../models/NewJoinSessionData';
import { validateName, validateGender } from '../../utils/validations';

export interface AddAssociateFormStatus {
  firstName: FieldStatus<string>;
  lastName: FieldStatus<string>;
  gender: FieldStatus<GenderCode | null>;
  dateOfBirth: FieldStatus<string>;
  virtualId?: FieldStatus<string>;
  roleType: FieldStatus<RoleType | null>;
}

export interface FieldStatus<T> {
  value?: T;
  error: boolean;
  errorMessage: string | null;
  changed: boolean;
}

export const generateAddAssociateStatus: (data?: AssociateInfo | undefined) => AddAssociateFormStatus = (data) => {
  if (data === undefined) {
    return {
      firstName: { value: '', error: false, errorMessage: null, changed: false },
      lastName: { value: '', error: false, errorMessage: null, changed: false },
      gender: { value: null, error: false, errorMessage: null, changed: false },
      dateOfBirth: { value: '', error: false, errorMessage: null, changed: false },
      virtualId: { value: '', error: false, errorMessage: null, changed: false },
      roleType: { value: null, error: false, errorMessage: null, changed: false },
    };
  } else {
    return {
      firstName: { value: data.firstName ?? '', error: false, errorMessage: null, changed: false },
      lastName: { value: data.lastName ?? '', error: false, errorMessage: null, changed: false },
      gender: { value: data.gender ?? null, error: false, errorMessage: null, changed: false },
      dateOfBirth: {
        value: data.dateOfBirth ?? '',
        error: false,
        errorMessage: null,
        changed: false,
      },
      virtualId: { value: data.virtualId ?? '', error: false, errorMessage: null, changed: false },
      roleType: { value: data.roleType ?? null, error: false, errorMessage: null, changed: false },
    };
  }
};

export const validateField: (field: string, value: string | null) => FieldStatus<string | null> | null = (
  field,
  value
) => {
  let err: string | null = null;

  switch (field) {
    case 'firstName':
      const assignFirstName: string = (value as string)
        //stop user from typing invalid characters
        .replace(/[^A-Za-z\s'\-]/g, '')
        // replace multiple spaces with a single space
        .replace(/\s+/g, ' ')
        .trim()
        .substring(0, 20);
      err = validateName(assignFirstName);
      return { value: assignFirstName, changed: true, error: err !== null, errorMessage: err };
    case 'lastName':
      const assignLastName: string = (value as string)
        //stop user from typing invalid characters
        .replace(/[^A-Za-z\s'\-]/g, '')
        // replace multiple spaces with a single space
        .replace(/\s+/g, ' ')
        .trim()
        .substring(0, 20);
      err = validateName(assignLastName);
      return { value: assignLastName, changed: true, error: err !== null, errorMessage: err };
    case 'gender':
      const assignGender = value as GenderCode;
      err = validateGender(assignGender);
      return { value: assignGender, changed: true, error: err !== null, errorMessage: err };
    case 'dateOfBirth':
      const assignDOB = value as string;
      // err = validateDOB(assignDOB);
      return { value: assignDOB, changed: true, error: err !== null, errorMessage: err };
    case 'roleType':
      return { value, changed: true, error: false, errorMessage: err };
    default:
      return null;
  }
};

export const convertAssociateFormToAssociateInfo = (fieldStatus: AddAssociateFormStatus): AssociateInfo => {
  return {
    virtualId: fieldStatus.virtualId?.value ?? '',
    firstName: fieldStatus.firstName?.value ?? '',
    lastName: fieldStatus.lastName?.value ?? '',
    dateOfBirth: fieldStatus.dateOfBirth?.value ?? '',
    gender: fieldStatus.gender?.value ?? GenderCode.X,
    roleType: fieldStatus.roleType?.value ?? RoleType.ASSOCIATE,
  };
};
